/**
 * AES-128-ECB + PKCS7，与 CryptoJS（WordArray 密钥）的 Base64 密文兼容。
 * 浏览器 Web Crypto 不支持 ECB，故用纯 JS 实现以去掉 crypto-js。
 */

const SBOX = new Uint8Array([
  99, 124, 119, 123, 242, 107, 111, 197, 48, 1, 103, 43, 254, 215, 171, 118, 202,
  130, 201, 125, 250, 89, 71, 240, 173, 212, 162, 175, 156, 164, 114, 192, 183,
  253, 147, 38, 54, 63, 247, 204, 52, 165, 229, 241, 113, 216, 49, 21, 4, 199,
  35, 195, 24, 150, 5, 154, 7, 18, 128, 226, 235, 39, 178, 117, 9, 131, 44, 26,
  27, 110, 90, 160, 82, 59, 214, 179, 41, 227, 47, 132, 83, 209, 0, 237, 32, 252,
  177, 91, 106, 203, 190, 57, 74, 76, 88, 207, 208, 239, 170, 251, 67, 77, 51,
  133, 69, 249, 2, 127, 80, 60, 159, 168, 81, 163, 64, 143, 146, 157, 56, 245,
  188, 182, 218, 33, 16, 255, 243, 210, 205, 12, 19, 236, 95, 151, 68, 23, 196,
  167, 126, 61, 100, 93, 25, 115, 96, 129, 79, 220, 34, 42, 144, 136, 70, 238,
  184, 20, 222, 94, 11, 219, 224, 50, 58, 10, 73, 6, 36, 92, 194, 211, 172, 98,
  145, 149, 228, 121, 231, 200, 55, 109, 141, 213, 78, 169, 108, 86, 244, 234,
  101, 122, 174, 8, 186, 120, 37, 46, 28, 166, 180, 198, 232, 221, 116, 31, 75,
  189, 139, 138, 112, 62, 181, 102, 72, 3, 246, 14, 97, 53, 87, 185, 134, 193,
  29, 158, 225, 248, 152, 17, 105, 217, 142, 148, 155, 30, 135, 233, 206, 85, 40,
  223, 140, 161, 137, 13, 191, 230, 66, 104, 65, 153, 45, 15, 176, 84, 187, 22,
]);

const INV_SBOX = new Uint8Array(256);
for (let i = 0; i < 256; i++) INV_SBOX[SBOX[i]] = i;

const RCON = [0x00, 0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36];

function mul2(n) {
  return ((n << 1) ^ (n & 0x80 ? 0x1b : 0)) & 0xff;
}

function mul(a, b) {
  let p = 0;
  for (let i = 0; i < 8; i++) {
    if (b & 1) p ^= a;
    const hi = a & 0x80;
    a = (a << 1) & 0xff;
    if (hi) a ^= 0x1b;
    b >>= 1;
  }
  return p;
}

function expandKey(keyBytes) {
  const w = new Uint8Array(176);
  w.set(keyBytes);
  for (let i = 4; i < 44; i++) {
    let t0 = w[(i - 1) * 4];
    let t1 = w[(i - 1) * 4 + 1];
    let t2 = w[(i - 1) * 4 + 2];
    let t3 = w[(i - 1) * 4 + 3];
    if (i % 4 === 0) {
      const tmp = t0;
      t0 = SBOX[t1] ^ RCON[i / 4];
      t1 = SBOX[t2];
      t2 = SBOX[t3];
      t3 = SBOX[tmp];
    }
    w[i * 4] = w[(i - 4) * 4] ^ t0;
    w[i * 4 + 1] = w[(i - 4) * 4 + 1] ^ t1;
    w[i * 4 + 2] = w[(i - 4) * 4 + 2] ^ t2;
    w[i * 4 + 3] = w[(i - 4) * 4 + 3] ^ t3;
  }
  return w;
}

function addRoundKey(state, w, round) {
  const off = round * 16;
  for (let i = 0; i < 16; i++) state[i] ^= w[off + i];
}

function subBytes(state) {
  for (let i = 0; i < 16; i++) state[i] = SBOX[state[i]];
}

function invSubBytes(state) {
  for (let i = 0; i < 16; i++) state[i] = INV_SBOX[state[i]];
}

function shiftRows(state) {
  let t = state[1];
  state[1] = state[5];
  state[5] = state[9];
  state[9] = state[13];
  state[13] = t;
  t = state[2];
  state[2] = state[10];
  state[10] = t;
  t = state[6];
  state[6] = state[14];
  state[14] = t;
  t = state[15];
  state[15] = state[11];
  state[11] = state[7];
  state[7] = state[3];
  state[3] = t;
}

function invShiftRows(state) {
  let t = state[13];
  state[13] = state[9];
  state[9] = state[5];
  state[5] = state[1];
  state[1] = t;
  t = state[2];
  state[2] = state[10];
  state[10] = t;
  t = state[6];
  state[6] = state[14];
  state[14] = t;
  t = state[3];
  state[3] = state[7];
  state[7] = state[11];
  state[11] = state[15];
  state[15] = t;
}

function mixColumns(state) {
  for (let c = 0; c < 4; c++) {
    const i = c * 4;
    const a0 = state[i];
    const a1 = state[i + 1];
    const a2 = state[i + 2];
    const a3 = state[i + 3];
    state[i] = mul2(a0) ^ (mul2(a1) ^ a1) ^ a2 ^ a3;
    state[i + 1] = a0 ^ mul2(a1) ^ (mul2(a2) ^ a2) ^ a3;
    state[i + 2] = a0 ^ a1 ^ mul2(a2) ^ (mul2(a3) ^ a3);
    state[i + 3] = (mul2(a0) ^ a0) ^ a1 ^ a2 ^ mul2(a3);
  }
}

function invMixColumns(state) {
  for (let c = 0; c < 4; c++) {
    const i = c * 4;
    const a0 = state[i];
    const a1 = state[i + 1];
    const a2 = state[i + 2];
    const a3 = state[i + 3];
    state[i] = mul(a0, 0x0e) ^ mul(a1, 0x0b) ^ mul(a2, 0x0d) ^ mul(a3, 0x09);
    state[i + 1] = mul(a0, 0x09) ^ mul(a1, 0x0e) ^ mul(a2, 0x0b) ^ mul(a3, 0x0d);
    state[i + 2] = mul(a0, 0x0d) ^ mul(a1, 0x09) ^ mul(a2, 0x0e) ^ mul(a3, 0x0b);
    state[i + 3] = mul(a0, 0x0b) ^ mul(a1, 0x0d) ^ mul(a2, 0x09) ^ mul(a3, 0x0e);
  }
}

function encryptBlock(input, w) {
  const state = new Uint8Array(input);
  addRoundKey(state, w, 0);
  for (let round = 1; round < 10; round++) {
    subBytes(state);
    shiftRows(state);
    mixColumns(state);
    addRoundKey(state, w, round);
  }
  subBytes(state);
  shiftRows(state);
  addRoundKey(state, w, 10);
  return state;
}

function decryptBlock(input, w) {
  const state = new Uint8Array(input);
  addRoundKey(state, w, 10);
  for (let round = 9; round >= 1; round--) {
    invShiftRows(state);
    invSubBytes(state);
    addRoundKey(state, w, round);
    invMixColumns(state);
  }
  invShiftRows(state);
  invSubBytes(state);
  addRoundKey(state, w, 0);
  return state;
}

function pkcs7Pad(data) {
  const pad = 16 - (data.length % 16);
  const out = new Uint8Array(data.length + pad);
  out.set(data);
  out.fill(pad, data.length);
  return out;
}

function pkcs7Unpad(data) {
  if (!data.length) throw new Error("invalid padding");
  const pad = data[data.length - 1];
  if (pad < 1 || pad > 16) throw new Error("invalid padding");
  for (let i = data.length - pad; i < data.length; i++) {
    if (data[i] !== pad) throw new Error("invalid padding");
  }
  return data.subarray(0, data.length - pad);
}

function utf8Encode(str) {
  return new TextEncoder().encode(str);
}

function utf8Decode(bytes) {
  return new TextDecoder().decode(bytes);
}

function toBase64(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function fromBase64(str) {
  const binary = atob(str);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function fromHex(hex) {
  const clean = hex.replace(/\s/g, "");
  if (clean.length % 2 !== 0) throw new Error("invalid hex");
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.substr(i * 2, 2), 16);
  }
  return out;
}

function keyFromUtf8(keyStr) {
  const keyBytes = utf8Encode(keyStr);
  if (keyBytes.length !== 16) {
    throw new Error("AES-128 key must be 16 bytes");
  }
  return expandKey(keyBytes);
}

function processBlocks(bytes, w, blockFn) {
  const out = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 16) {
    out.set(blockFn(bytes.subarray(i, i + 16), w), i);
  }
  return out;
}

export function aesEncryptToBase64(plainText, keyStr) {
  const w = keyFromUtf8(keyStr);
  const padded = pkcs7Pad(utf8Encode(plainText));
  return toBase64(processBlocks(padded, w, encryptBlock));
}

export function aesDecryptFromBase64OrHex(cipherText, keyStr) {
  const w = keyFromUtf8(keyStr);
  let bytes;
  const looksHex =
    typeof cipherText === "string" &&
    /^[0-9a-fA-F]+$/.test(cipherText) &&
    cipherText.length % 32 === 0;
  if (looksHex) {
    try {
      bytes = fromHex(cipherText);
    } catch (e) {
      bytes = fromBase64(cipherText);
    }
  } else {
    try {
      bytes = fromBase64(cipherText);
    } catch (e) {
      bytes = fromHex(cipherText);
    }
  }
  if (bytes.length % 16 !== 0) throw new Error("invalid ciphertext length");
  return utf8Decode(pkcs7Unpad(processBlocks(bytes, w, decryptBlock)));
}
